/**
 * DirectoryMatches.jsx — "who else matches in the Microsoft 365 directory".
 *
 * All users lists everyone WITH access (signed in or not). Under the table this
 * runs the same search against the live directory (/api/access/directory) and
 * shows the matching people who have NO AE Tracker access, with a Give access
 * shortcut — `listedEmails` is everyone the page already knows has access.
 *
 * Access comes from the lists the page already loaded — roleByEmail (direct)
 * and inheritedByEmail (the bulk group-member map) — so this adds one Graph
 * search per pause in typing and nothing else.
 */
import React, { useEffect, useRef, useState } from 'react';
import { searchDirectory } from '../api/client.js';

const DEBOUNCE_MS = 300;

export default function DirectoryMatches({ query, listedEmails, roleByEmail, inheritedByEmail, pinByEmail, onGrant }) {
  const [people, setPeople] = useState([]);
  const [status, setStatus] = useState('idle');   // idle | loading | done | error
  const seqRef = useRef(0);
  const q = String(query || '').trim();

  useEffect(() => {
    if (q.length < 2) { setPeople([]); setStatus('idle'); return undefined; }
    const seq = ++seqRef.current;
    setStatus('loading');
    const t = setTimeout(async () => {
      try {
        const res = await searchDirectory(q.split('@')[0]);
        if (seq !== seqRef.current) return;
        setPeople(res?.users || []); setStatus('done');
      } catch {
        if (seq !== seqRef.current) return;
        setPeople([]); setStatus('error');
      }
    }, DEBOUNCE_MS);
    return () => clearTimeout(t);
  }, [q]);

  if (q.length < 2 || status === 'idle') return null;
  const extra = people.filter(p => !listedEmails.has(p.email));
  if (status === 'done' && !extra.length) return null;

  return (
    <div style={{ marginTop: 14 }}>
      <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-2)' }}>Not in AE Tracker yet — Microsoft 365 directory</div>
      <div style={{ fontSize: 11, color: 'var(--text-3)', margin: '2px 0 8px' }}>
        People matching “{q}” in the directory who don’t have AE Tracker access.
      </div>
      {status === 'loading' && !extra.length && (
        <div style={{ fontSize: 12, color: 'var(--text-3)' }}>Searching the directory…</div>
      )}
      {status === 'error' && (
        <div style={{ fontSize: 12, color: 'var(--text-3)' }}>Directory search is unavailable right now.</div>
      )}
      <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'grid', gap: 6 }}>
        {extra.map(p => {
          const direct = roleByEmail[p.email];
          const inh = inheritedByEmail[p.email];
          const pin = pinByEmail[p.email];
          const access = direct
            ? `${direct} · direct`
            : inh ? `${inh.roleLabel || inh.role} · via ${inh.groupName}` : null;
          return (
            <li key={p.id || p.email} style={{
              display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap',
              padding: '7px 10px', border: '1px solid var(--border)', borderRadius: 8, background: 'var(--surface)',
            }}>
              <div style={{ minWidth: 0, flex: '1 1 220px' }}>
                <div style={{ fontSize: 12.5, fontWeight: 500 }}>{p.name || p.email}</div>
                <div style={{ fontSize: 11, color: 'var(--text-3)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                  title={p.email}>
                  {[p.email, p.jobTitle, p.office].filter(Boolean).join(' · ')}
                </div>
              </div>
              <div style={{ fontSize: 12, flex: '0 1 220px', color: access ? 'var(--text)' : 'var(--text-3)' }}>
                {access || 'No AE Tracker access'}
                <div style={{ fontSize: 11, color: 'var(--text-3)' }}>
                  {pin ? 'PIN set' : access ? 'Not signed in yet — no PIN' : 'Not in AE Tracker'}
                </div>
              </div>
              {!access && onGrant && (
                <button className="btn sm" type="button" onClick={() => onGrant(p.email)} style={{ fontSize: 11 }}>
                  Give access
                </button>
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );
}
